import React, { useEffect, useReducer, useState } from 'react';
import {
    Modal,
    Form,
    Button,
    Input,
    message,
    Divider,
    Table,
    InputNumber,
    Typography,
    Popconfirm,
    Checkbox,
    Tooltip,
    Space,
} from 'antd';
import { EditTwoTone, DeleteTwoTone, SearchOutlined } from '@ant-design/icons';
import useSWRImmutable from 'swr/immutable';
import { v4 as uuidv4 } from 'uuid';
import Highlighter from 'react-highlight-words';
import {
    CANCEL_CONFIRMATION_MESSAGE,
    DELETE_CONFIRMATION_MESSAGE,
    GENERIC_ERROR_MESSAGE,
    validateMessages,
} from '../../constants/feedback';
import {
    CONJUNTO_BUSQUEDAS_ENDPOINT,
    createConjuntoBusqueda,
    getConjuntoBusquedaById,
    updateConjuntoBusqueda,
} from '../../services/api/conjunto-busqueda';

const itemLayout = {
    labelCol: { span: 24 },
};

const EditableCell = ({
    editing,
    dataIndex,
    title,
    inputType,
    record,
    index,
    children,
    ...restProps
}) => {
    let inputNode = <Input />;

    switch (inputType) {
        case 'boolean':
            inputNode = <Checkbox />;
            break;
        case 'number':
            inputNode = <InputNumber />;
            break;
        default:
            break;
    }

    return (
        <td {...restProps}>
            {editing ? (
                <Form.Item
                    name={dataIndex}
                    valuePropName={
                        inputType === 'boolean' ? 'checked' : undefined
                    }
                    style={{
                        margin: 0,
                    }}
                    rules={[
                        {
                            required: true,
                        },
                    ]}
                >
                    {inputNode}
                </Form.Item>
            ) : (
                children
            )}
        </td>
    );
};

const ConjuntoBusquedaItem = (props) => {
    const { isModalOpen, idConjunto, onSave, onCancel } = props;
    const [dataSource, setDataSource] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [editingKey, setEditingKey] = useState('');
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    const searchInput = useReducer(null);
    const pageSize = 5;
    const [form] = Form.useForm();

    const { data: conjuntoBusqueda, mutate } = useSWRImmutable(
        idConjunto > 0 ? [CONJUNTO_BUSQUEDAS_ENDPOINT, idConjunto] : null,
        ([_, idConjunto]) => getConjuntoBusquedaById(idConjunto),
        { revalidateOnMount: true }
    );

    useEffect(() => {
        if (idConjunto > 0 && conjuntoBusqueda?.data) {
            let fields = Object.keys(conjuntoBusqueda.data).map((key) => ({
                name: key,
                value: conjuntoBusqueda.data[key],
            }));

            form.setFields(fields);

            const data = conjuntoBusqueda.data.detalles.map((item) => ({
                ...item,
                key: uuidv4(),
            }));

            setDataSource(data);
        }
    }, [idConjunto, conjuntoBusqueda, form]);

    const handleCancelClick = () => {
        form.resetFields();
        onCancel();
    };

    const handleFinishClick = async (values) => {
        try {
            setIsLoading(true);

            const params = { ...values, idConjunto, detalles: dataSource };

            if (idConjunto > 0) {
                await updateConjuntoBusqueda(params);
                mutate();
            } else {
                await createConjuntoBusqueda(params);
            }

            form.resetFields();
            onSave();
        } catch (error) {
            message.error(GENERIC_ERROR_MESSAGE);
        } finally {
            setIsLoading(false);
        }
    };

    const isEditing = (record) => record.key === editingKey;

    const handleEditPalabra = (record) => {
        form.setFieldsValue({
            palabra: '',
            flagPalabraPositiva: '',
            orden: '',
            ...record,
        });

        setEditingKey(record.key);
    };

    const handleCancelPalabra = () => {
        setEditingKey('');
    };

    const handleAddPalabra = () => {
        const newData = {
            key: uuidv4(),
            palabra: '<<DEFINIR PALABRA>>',
            flagPalabraPositiva: false,
            orden: '0',
        };

        setDataSource([...dataSource, newData]);

        let lastPage = Math.ceil(dataSource.length / pageSize);
        lastPage = lastPage <= 0 ? 1 : lastPage;

        setCurrentPage(lastPage);
    };

    const handleSavePalabra = async (key) => {
        try {
            const row = await form.validateFields();
            const newData = [...dataSource];
            const index = newData.findIndex((item) => key === item.key);

            if (index > -1) {
                const item = newData[index];
                newData.splice(index, 1, {
                    ...item,
                    ...row,
                });
                setDataSource(newData);
                setEditingKey('');
            } else {
                newData.push(row);
                setDataSource(newData);
                setEditingKey('');
            }
        } catch (errInfo) {
            console.log('Validate Failed:', errInfo);
        }
    };

    const handleDeletePalabra = (key) => {
        const newData = dataSource.filter((item) => item.key !== key);
        setDataSource(newData);
    };

    const handleSearch = (selectedKeys, confirm, dataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };
    const handleReset = (clearFilters) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({
            setSelectedKeys,
            selectedKeys,
            confirm,
            clearFilters,
            close,
        }) => (
            <div
                style={{
                    padding: 8,
                }}
                onKeyDown={(e) => e.stopPropagation()}
            >
                <Input
                    ref={searchInput}
                    placeholder={`Buscar ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) =>
                        setSelectedKeys(e.target.value ? [e.target.value] : [])
                    }
                    onPressEnter={() =>
                        handleSearch(selectedKeys, confirm, dataIndex)
                    }
                    style={{
                        marginBottom: 8,
                        display: 'block',
                    }}
                />
                <Space>
                    <Button
                        type='primary'
                        onClick={() =>
                            handleSearch(selectedKeys, confirm, dataIndex)
                        }
                        icon={<SearchOutlined />}
                        size='small'
                        style={{
                            width: 90,
                        }}
                    >
                        Buscar
                    </Button>
                    <Button
                        onClick={() =>
                            clearFilters && handleReset(clearFilters)
                        }
                        size='small'
                        style={{
                            width: 90,
                        }}
                    >
                        Limpiar
                    </Button>
                    <Button
                        type='link'
                        size='small'
                        onClick={() => {
                            confirm({
                                closeDropdown: false,
                            });
                            setSearchText(selectedKeys[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filtrar
                    </Button>
                    <Button
                        type='link'
                        size='small'
                        onClick={() => {
                            close();
                        }}
                    >
                        Cerrar
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => (
            <SearchOutlined
                style={{
                    color: filtered ? '#1677ff' : undefined,
                }}
            />
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes(value.toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{
                        backgroundColor: '#ffc069',
                        padding: 0,
                    }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const columns = [
        {
            title: 'Palabra',
            dataIndex: 'palabra',
            sorter: (a, b) => (a.palabra < b.palabra ? -1 : 1),
            width: '40%',
            editable: true,
            ...getColumnSearchProps('palabra'),
        },
        {
            title: 'Positiva',
            dataIndex: 'flagPalabraPositiva',
            sorter: (a, b) => a.flagPalabraPositiva - b.flagPalabraPositiva,
            width: '20%',
            editable: true,
            render: (value) => (value ? 'Si' : 'No'),
        },
        {
            title: 'Orden',
            dataIndex: 'orden',
            sorter: (a, b) => a.orden - b.orden,
            width: '20%',
            editable: true,
        },
        {
            title: 'Acción',
            dataIndex: 'accion',
            render: (_, record) => {
                const editable = isEditing(record);
                return editable ? (
                    <span>
                        <Typography.Link
                            onClick={() => handleSavePalabra(record.key)}
                            style={{
                                marginRight: 8,
                            }}
                        >
                            Aceptar
                        </Typography.Link>
                        <Popconfirm
                            title={CANCEL_CONFIRMATION_MESSAGE}
                            onConfirm={handleCancelPalabra}
                        >
                            <Typography.Link>Cancelar</Typography.Link>
                        </Popconfirm>
                    </span>
                ) : (
                    <Space size={0}>
                        <Tooltip title='Editar'>
                            <EditTwoTone
                                onClick={() => handleEditPalabra(record)}
                            />
                        </Tooltip>

                        <Divider type='vertical' />

                        <Popconfirm
                            title={DELETE_CONFIRMATION_MESSAGE}
                            onConfirm={() => handleDeletePalabra(record.key)}
                        >
                            <Tooltip
                                title='Eliminar'
                                className='cursor-pointer'
                            >
                                <DeleteTwoTone />
                            </Tooltip>
                        </Popconfirm>
                    </Space>
                );
            },
        },
    ];

    const mergedColumns = columns.map((col) => {
        if (!col.editable) {
            return col;
        }

        let inputType = 'text';

        switch (col.dataIndex) {
            case 'flagPalabraPositiva':
                inputType = 'boolean';
                break;
            case 'orden':
                inputType = 'number';
                break;
            default:
                break;
        }

        return {
            ...col,
            onCell: (record) => ({
                record,
                inputType: inputType,
                dataIndex: col.dataIndex,
                title: col.title,
                editing: isEditing(record),
            }),
        };
    });

    const handleTableChange = (pagination, filters, sorter) => {
        setCurrentPage(pagination.current);
    };

    return (
        <Modal
            open={isModalOpen}
            title='Conjunto de búsqueda'
            onCancel={handleCancelClick}
            destroyOnClose={true}
            footer={[
                <Button key='back' onClick={handleCancelClick}>
                    Cancelar
                </Button>,
                <Button
                    type='primary'
                    onClick={form.submit}
                    loading={isLoading}
                >
                    Guardar
                </Button>,
            ]}
            style={{ top: 20 }}
            width={800}
        >
            <Form
                form={form}
                onFinish={handleFinishClick}
                validateMessages={validateMessages}
                autoComplete='off'
            >
                <Divider className='mb-2'></Divider>

                <div className='row pl-2 pr-2'>
                    <div className='col-lg-12'>
                        <Form.Item
                            name='descripcion'
                            label='Descripción'
                            {...itemLayout}
                            rules={[
                                {
                                    required: true,
                                    whitespace: true,
                                },
                            ]}
                        >
                            <Input maxLength={50} allowClear />
                        </Form.Item>
                    </div>

                    <div className='col-lg-12'>
                        <Button
                            onClick={handleAddPalabra}
                            type='primary'
                            style={{
                                marginBottom: 16,
                            }}
                        >
                            Agregar palabra
                        </Button>
                        <Table
                            components={{
                                body: {
                                    cell: EditableCell,
                                },
                            }}
                            dataSource={dataSource}
                            columns={mergedColumns}
                            rowClassName='editable-row'
                            pagination={{
                                defaultPageSize: pageSize,
                                current: currentPage,
                                onChange: handleTableChange,
                            }}
                            loading={isLoading}
                        ></Table>
                    </div>
                </div>
            </Form>
        </Modal>
    );
};

export default ConjuntoBusquedaItem;
